import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Users, Zap, ArrowUpRight, Flame } from 'lucide-react';
import { formatCurrency, formatTimeRemaining } from '../../lib/utils';
import { cn } from '../../lib/utils';

export default function FlashDealCard({ deal, featured = false }) {
  const [timeLeft, setTimeLeft] = useState(formatTimeRemaining(deal.end_time));
  
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(formatTimeRemaining(deal.end_time));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [deal.end_time]);

  const claimsPercent = (deal.claims_count / deal.max_claims) * 100;
  const isUrgent = claimsPercent > 70 || timeLeft.urgent;
  
  if (timeLeft.expired) return null;

  return (
    <Link 
      to={`/flash-deal/${deal.id}`}
      className={cn(
        "deal-card block rounded-2xl group",
        featured && "deal-card-featured"
      )}
      data-testid={`flash-deal-card-${deal.id}`}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden rounded-t-2xl bg-zinc-100">
        {deal.product_image ? (
          <img 
            src={deal.product_image} 
            alt={deal.product_name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#b8ff00]/20 to-[#00ff88]/20">
            <Zap size={48} className="text-[#b8ff00]" />
          </div>
        )}
        
        {/* Discount Badge */}
        <div className="absolute top-3 left-3 discount-badge px-3 py-1.5 rounded-full">
          <span className="font-display font-bold text-sm">-{deal.discount_percent}%</span>
        </div>
        
        {/* Timer */}
        <div className={cn(
          "absolute top-3 right-3 px-3 py-1.5 rounded-full flex items-center gap-1.5",
          timeLeft.urgent ? "timer-badge text-white" : "bg-white/90 backdrop-blur-sm text-zinc-900"
        )}>
          <Clock size={12} />
          <span className="font-mono text-xs font-bold">{timeLeft.text}</span>
        </div>

        {/* Featured Badge */}
        {featured && (
          <div className="absolute bottom-3 left-3 bg-zinc-900 text-white px-3 py-1 rounded-full flex items-center gap-1.5">
            <Flame size={12} className="text-orange-400" />
            <span className="text-xs font-semibold">Hot Deal</span>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        <p className="text-xs text-zinc-500 mb-1">by {deal.vendor_name}</p>
        <h3 className="font-semibold text-zinc-900 truncate mb-2 group-hover:text-[#00aa55] transition-colors">
          {deal.product_name}
        </h3>
        
        {/* Price */}
        <div className="flex items-baseline gap-2 mb-3">
          <span className="font-display font-bold text-xl text-zinc-900">
            {formatCurrency(deal.flash_price)}
          </span>
          <span className="text-sm text-zinc-400 line-through">
            {formatCurrency(deal.original_price)}
          </span>
        </div>
        
        {/* Inventory Bar */}
        <div className="mb-2">
          <div className="flex items-center justify-between text-xs text-zinc-500 mb-1.5">
            <div className="flex items-center gap-1">
              <Users size={12} />
              <span>{deal.claims_count} claimed</span>
            </div>
            <span className={cn("font-semibold", isUrgent && "text-orange-500")}>
              {deal.max_claims - deal.claims_count} left
            </span>
          </div>
          <div className="inventory-bar">
            <div 
              className={cn("inventory-fill", isUrgent && "urgent")}
              style={{ width: `${claimsPercent}%` }}
            />
          </div>
        </div>
      </div>
    </Link>
  );
}
